import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, HandHeart } from "@phosphor-icons/react";
import { analytics } from "./analytics";

/**
 * Warm Welcome — a small, one-time-per-session invitation that gently points
 * at the existing Begin Again Guide. It is NOT a second chatbot; clicking it
 * simply calls `onOpenGuide()` to open the same Guide.
 *
 * Rules:
 *  - Only appears on supportive/content-oriented pages listed in ELIGIBLE_ROUTES.
 *  - Waits DELAY_MS (~20s) on the page before showing.
 *  - Shows once per browser session. Dismissal or opening the Guide (from any
 *    source) suppresses it for the remainder of the session.
 *  - Uses sessionStorage flags — no persistent cookies.
 */

const DELAY_MS = 20000;
const SS_SHOWN = "bag_ww_shown";
const SS_DISMISSED = "bag_ww_dismissed";
const SS_SUPPRESSED = "bag_ww_suppressed";

// Whitelist of content/support pages where the invitation is welcome.
// Homepage, /donate, /donate/success, /contact and hidden/system routes are
// intentionally excluded (marketing hero, active checkout, active form input).
const ELIGIBLE_ROUTES = new Set([
  "/about",
  "/grief-support",
  "/from-diagnosis-to-goodbye",
  "/resources",
  "/ask-chaplain-derrick",
  "/classes",
  "/request-support",
  "/books",
  "/bulk-orders",
]);

function safeSession() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function readFlag(key) {
  const s = safeSession();
  return s ? s.getItem(key) === "1" : false;
}

function writeFlag(key) {
  const s = safeSession();
  if (s) {
    try {
      s.setItem(key, "1");
    } catch {
      /* ignore */
    }
  }
}

/** Public helper: mark the Warm Welcome as suppressed (e.g. the user opened
 *  the Guide voluntarily from the FAB). */
export function suppressWarmWelcome() {
  writeFlag(SS_SUPPRESSED);
}

const WarmWelcome = ({ onOpenGuide }) => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const eligible = useMemo(() => ELIGIBLE_ROUTES.has(pathname), [pathname]);

  // Has the invitation already been shown or explicitly suppressed this session?
  const alreadyHandled = useCallback(
    () =>
      readFlag(SS_SHOWN) ||
      readFlag(SS_DISMISSED) ||
      readFlag(SS_SUPPRESSED),
    []
  );

  // Schedule the appearance on eligible routes.
  useEffect(() => {
    // Clear any pending timer whenever the route changes.
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Hide any currently-visible invitation if the user navigates elsewhere.
    setVisible(false);

    if (!eligible) return;
    if (alreadyHandled()) return;

    timerRef.current = setTimeout(() => {
      // Re-check right before showing (Guide may have opened during the wait).
      if (alreadyHandled()) return;
      setVisible(true);
      writeFlag(SS_SHOWN);
      analytics.warmWelcomeShown();
    }, DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [eligible, pathname, alreadyHandled]);

  const dismiss = useCallback(() => {
    writeFlag(SS_DISMISSED);
    analytics.warmWelcomeDismissed();
    setVisible(false);
  }, []);

  const openGuide = useCallback(() => {
    writeFlag(SS_SUPPRESSED);
    analytics.warmWelcomeOpened();
    setVisible(false);
    onOpenGuide();
  }, [onOpenGuide]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="bag-ww-title"
      data-testid="warm-welcome"
      className="animate-[bag-ww-in_260ms_ease-out] fixed bottom-24 right-4 z-40 w-[290px] rounded-2xl border border-primary/20 bg-white p-4 pr-5 shadow-[0_18px_40px_-12px_rgba(91,122,148,0.35)] sm:bottom-28 sm:right-6"
    >
      {/* Subtle keyframes injected inline so we don't need to touch global CSS. */}
      <style>{`@keyframes bag-ww-in { from { opacity:0; transform: translateY(8px);} to { opacity:1; transform: translateY(0);} }`}</style>

      <button
        type="button"
        onClick={dismiss}
        data-testid="warm-welcome-dismiss"
        aria-label="Dismiss invitation"
        className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-ink-400 transition-colors hover:bg-softblue hover:text-primary"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-softblue text-primary">
          <HandHeart size={16} weight="duotone" />
        </span>
        <div className="min-w-0">
          <div
            id="bag-ww-title"
            className="font-heading text-sm font-medium leading-snug text-ink-900"
          >
            Not sure where to start?
          </div>
          <p className="mt-1 text-xs leading-snug text-ink-600">
            I can help you find support, resources, or someone to talk with.
          </p>
          <button
            type="button"
            onClick={openGuide}
            data-testid="warm-welcome-open"
            className="mt-3 inline-flex items-center justify-center rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-dark"
          >
            Help Me Find Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarmWelcome;
